import React, { useState, useEffect } from 'react';
import { Box, Tab, Tabs, Typography, CircularProgress, Alert, Paper } from '@mui/material';

import { useTranslation } from 'react-i18next';
import CompanyUsersTab from '../components/company/CompanyUsersTab';
import CompanyInvitationsTab from '../components/company/CompanyInvitationsTab';
import CompanyLicensesTab from '../components/company/CompanyLicensesTab';
import CompanyNetworkSettingsTab from '../components/company/CompanyNetworkSettingsTab';
import { getCompanyInfo, readAxiosDetail, type CompanyInfo } from '../components/company/api_calls';

import { Icon } from '@liftango/ui-icons';
function TabPanel({ children, value, index }: { children: React.ReactNode; value: number; index: number }) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ pt: 3 }}>
      {value === index && children}
    </Box>
  );
}

export default function CompanyPage() {
  const { t } = useTranslation('company');
  const [tab, setTab] = useState(0);
  const [info, setInfo] = useState<CompanyInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadInfo = async () => {
    try {
      const data = await getCompanyInfo();
      setInfo(data);
    } catch (e) {
      setError(readAxiosDetail(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void loadInfo(); }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', px: 3, py: 4 }}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Typography variant="h5" fontWeight={600} gutterBottom>
        {info?.customer_name || t('page.defaultName')}
      </Typography>

      {info && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <StatCard icon={<Icon size={20} name="users" />} label={t('page.stats.users')} value={info.user_count} />
          <StatCard
            icon={<Icon size={20} name="id-card" />}
            label={t('page.stats.licenses')}
            value={t('page.stats.licenseValue', { used: info.license_count, total: info.license_total })}
          />
        </Box>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label={t('tabs.users')} />
          <Tab label={t('tabs.invitations')} />
          <Tab label={t('tabs.licenses')} />
          <Tab label={t('tabs.network')} />
        </Tabs>
      </Box>

      <TabPanel value={tab} index={0}>
        <CompanyUsersTab />
      </TabPanel>
      <TabPanel value={tab} index={1}>
        <CompanyInvitationsTab />
      </TabPanel>
      <TabPanel value={tab} index={2}>
        <CompanyLicensesTab onLicenseChange={() => void loadInfo()} />
      </TabPanel>
      <TabPanel value={tab} index={3}>
        <CompanyNetworkSettingsTab />
      </TabPanel>
    </Box>
  );
}

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: number | string }) {
  return (
    <Paper variant="outlined" sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
      {icon}
      <Box>
        <Typography variant="h6" fontWeight={600}>{value}</Typography>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
      </Box>
    </Paper>
  );
}
